import {
  Anchor,
  Badge,
  Button,
  Card,
  FileButton,
  Group,
  Modal,
  Pagination,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { unwrap } from '../api/client';
import { listQuery } from '../api/list';
import { useOrg } from '../api/org';
import {
  createDocument,
  createKnowledgeStore,
  deleteDocument,
  deleteKnowledgeStore,
  listDocuments,
  listKnowledgeStores,
  reindexDocument,
  searchKnowledge,
  type Document,
  type Store,
} from '../api/gen';
import { ProjectSelect, useProjectParam } from '../components/ProjectSelect';
import { Refusal, refusalCode } from '../components/Refusal';

const PAGE = 20;

const colours: Record<Document['status'], string> = { pending: 'blue', indexed: 'green', failed: 'red' };

/**
 * A project's corpus: its stores, their documents, and a search across them.
 *
 * Stores and their documents are rows and list whether or not the corpus is
 * configured. Adding, re-indexing and searching need an embedder, and without
 * one the server refuses them with 503 — so the first such refusal is kept at
 * the top of the page, where it explains the rest, rather than in whichever
 * form happened to meet it.
 */
export function KnowledgePage() {
  const queries = useQueryClient();
  const headers = useOrg();
  const [project, setProject] = useProjectParam();
  const [params, setParams] = useSearchParams();
  const storeId = params.get('store') ?? '';
  const select = (id: string) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('store', id);
      else next.delete('store');
      return next;
    });

  const [corpusOff, setCorpusOff] = useState<unknown>(null);
  const noteCorpus = (err: unknown) => {
    if (refusalCode(err) === 'NO_CORPUS') setCorpusOff(err);
  };

  const [storeOpened, storeModal] = useDisclosure();
  const [storeName, setStoreName] = useState('');
  const [storeDescription, setStoreDescription] = useState('');

  const stores = useQuery({
    queryKey: ['stores', project],
    queryFn: () => unwrap(listKnowledgeStores({ headers, path: { project } })),
    enabled: !!project,
  });

  const createStore = useMutation({
    mutationFn: () =>
      unwrap(
        createKnowledgeStore({
          headers,
          path: { project },
          body: { name: storeName, description: storeDescription || undefined },
        }),
      ),
    onSuccess: (s) => {
      queries.invalidateQueries({ queryKey: ['stores', project] });
      setStoreName('');
      setStoreDescription('');
      storeModal.close();
      select(s.id);
    },
  });

  const delStore = useMutation({
    mutationFn: (id: string) => unwrap(deleteKnowledgeStore({ headers, path: { id } })),
    onSuccess: (_, id) => {
      queries.invalidateQueries({ queryKey: ['stores', project] });
      if (id === storeId) select('');
    },
  });

  const store = stores.data?.stores.find((s) => s.id === storeId);

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Knowledge</Title>
        <Group>
          <ProjectSelect value={project} onChange={setProject} />
          <Button onClick={storeModal.open} disabled={!project}>
            New store
          </Button>
        </Group>
      </Group>

      <Text c="dimmed" size="sm">
        A store is a named corpus. An agent reads the ones its{' '}
        <code>agent.jsonc</code> names in <code>indexes</code>; a document is
        chunked and embedded in the background after it arrives.
      </Text>

      <Refusal error={corpusOff} />
      <Refusal error={stores.error} />
      <Refusal error={delStore.error} />

      <Card withBorder p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Store</Table.Th>
              <Table.Th>Documents</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {stores.isLoading && (
              <Table.Tr>
                <Table.Td colSpan={4}>Loading…</Table.Td>
              </Table.Tr>
            )}
            {stores.data?.stores.map((s) => (
              <Table.Tr
                key={s.id}
                onClick={() => select(s.id)}
                style={{ cursor: 'pointer' }}
                bg={s.id === storeId ? 'var(--mantine-primary-color-light)' : undefined}
              >
                <Table.Td>
                  <code>{s.name}</code>
                  <Text size="xs" c="dimmed">
                    {s.description}
                  </Text>
                </Table.Td>
                <Table.Td>{s.documentCount}</Table.Td>
                <Table.Td>
                  <Text size="sm">{new Date(s.createdAt).toLocaleString()}</Text>
                </Table.Td>
                <Table.Td>
                  <Group justify="flex-end">
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      loading={delStore.isPending && delStore.variables === s.id}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        if (confirm(`Delete ${s.name} and all ${s.documentCount} of its documents?`))
                          delStore.mutate(s.id);
                      }}
                    >
                      Delete
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {stores.data?.stores.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed">No knowledge stores in this project yet.</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {!project && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed">Make a project first.</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {store && <Documents store={store} onCorpusRefusal={noteCorpus} />}
      {project && <Search project={project} store={store} onCorpusRefusal={noteCorpus} />}

      <Modal opened={storeOpened} onClose={storeModal.close} title="New knowledge store">
        <Stack>
          <TextInput
            label="Name"
            description="What an agent's agent.jsonc names in its indexes."
            value={storeName}
            onChange={(e) => setStoreName(e.currentTarget.value)}
          />
          <TextInput
            label="Description"
            value={storeDescription}
            onChange={(e) => setStoreDescription(e.currentTarget.value)}
          />
          <Refusal error={createStore.error} />
          <Button disabled={!storeName} loading={createStore.isPending} onClick={() => createStore.mutate()}>
            Create
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}

function Documents({ store, onCorpusRefusal }: { store: Store; onCorpusRefusal: (err: unknown) => void }) {
  const queries = useQueryClient();
  const headers = useOrg();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [addOpened, addModal] = useDisclosure();
  const [title, setTitle] = useState('');
  const [source, setSource] = useState('');
  const [content, setContent] = useState('');
  const [viewing, setViewing] = useState<Document | null>(null);

  const docs = useQuery({
    queryKey: ['documents', store.id, search, page],
    queryFn: () =>
      unwrap(
        listDocuments({
          headers,
          path: { id: store.id },
          query: listQuery('documents', {
            search: search || undefined,
            sort: 'createdAt',
            sortDir: 'desc',
            limit: PAGE,
            offset: (page - 1) * PAGE,
          }),
        }),
      ),
    // Indexing is a background job; while anything on the page is pending,
    // look again until it is not.
    refetchInterval: (q) => (q.state.data?.data.some((d) => d.status === 'pending') ? 3_000 : false),
  });

  const refresh = () => {
    queries.invalidateQueries({ queryKey: ['documents', store.id] });
    queries.invalidateQueries({ queryKey: ['stores', store.projectId] });
  };

  const add = useMutation({
    mutationFn: () =>
      unwrap(
        createDocument({
          headers,
          path: { id: store.id },
          body: { title, content, source: source || undefined },
        }),
      ),
    onSuccess: () => {
      refresh();
      setTitle('');
      setSource('');
      setContent('');
      addModal.close();
    },
    onError: onCorpusRefusal,
  });

  const del = useMutation({
    mutationFn: (id: string) => unwrap(deleteDocument({ headers, path: { id } })),
    onSuccess: refresh,
  });

  const reindex = useMutation({
    mutationFn: (id: string) => unwrap(reindexDocument({ headers, path: { id } })),
    onSuccess: refresh,
    onError: onCorpusRefusal,
  });

  const load = async (file: File | null) => {
    if (!file) return;
    setContent(await file.text());
    setSource(file.name);
    if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));
  };

  const total = docs.data?.total ?? 0;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={4}>
          Documents in <code>{store.name}</code>
        </Title>
        <Group>
          <TextInput
            placeholder="Search titles"
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
            }}
            w={200}
          />
          <Button
            onClick={() => {
              add.reset();
              addModal.open();
            }}
          >
            Add document
          </Button>
        </Group>
      </Group>

      <Refusal error={docs.error} />
      <Refusal error={del.error} />
      <Refusal error={reindex.error} />

      <Card withBorder p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Title</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Chunks</Table.Th>
              <Table.Th>Added</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {docs.isLoading && (
              <Table.Tr>
                <Table.Td colSpan={5}>Loading…</Table.Td>
              </Table.Tr>
            )}
            {docs.data?.data.map((d) => (
              <Table.Tr key={d.id}>
                <Table.Td>
                  <Anchor component="button" onClick={() => setViewing(d)}>
                    {d.title}
                  </Anchor>
                  {d.source && (
                    <Text size="xs" c="dimmed">
                      {d.source}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" color={colours[d.status]}>
                    {d.status}
                  </Badge>
                  {d.error && (
                    <Text size="xs" c="red">
                      {d.error}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{d.chunkCount}</Table.Td>
                <Table.Td>
                  <Text size="sm">{new Date(d.createdAt).toLocaleString()}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    {d.status !== 'pending' && (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        loading={reindex.isPending && reindex.variables === d.id}
                        onClick={() => reindex.mutate(d.id)}
                      >
                        Reindex
                      </Button>
                    )}
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      loading={del.isPending && del.variables === d.id}
                      onClick={() => {
                        if (confirm(`Delete ${d.title}?`)) del.mutate(d.id);
                      }}
                    >
                      Delete
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {docs.data?.data.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed">{search ? 'Nothing matches.' : 'No documents in this store yet.'}</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {total > PAGE && (
        <Group justify="flex-end">
          <Pagination total={Math.ceil(total / PAGE)} value={page} onChange={setPage} size="sm" />
        </Group>
      )}

      <Modal opened={addOpened} onClose={addModal.close} title={`Add to ${store.name}`} size="lg">
        <Stack>
          <Group justify="space-between" align="flex-end">
            <TextInput
              label="Title"
              style={{ flex: 1 }}
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
            />
            <FileButton onChange={load} accept=".txt,.md,.markdown,.csv,.json,.html,text/*">
              {(props) => (
                <Button variant="light" {...props}>
                  Load a text file
                </Button>
              )}
            </FileButton>
          </Group>
          <TextInput
            label="Source"
            description="A filename or URL, for the citation. Optional."
            value={source}
            onChange={(e) => setSource(e.currentTarget.value)}
          />
          <Textarea
            label="Content"
            description="Plain text or Markdown. Headings become the passage's trail in search results."
            autosize
            minRows={8}
            maxRows={20}
            value={content}
            onChange={(e) => setContent(e.currentTarget.value)}
          />
          <Refusal error={add.error} />
          <Button disabled={!title || !content} loading={add.isPending} onClick={() => add.mutate()}>
            Add
          </Button>
        </Stack>
      </Modal>

      <Modal opened={!!viewing} onClose={() => setViewing(null)} title={viewing?.title} size="xl">
        {viewing && (
          <Stack>
            <Text size="xs" c="dimmed">
              {viewing.source || 'no source'} · {viewing.chunkCount} chunks
              {viewing.embeddingSpace ? ` · ${viewing.embeddingSpace}` : ''}
            </Text>
            <ScrollArea h={480}>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {viewing.content}
              </Text>
            </ScrollArea>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

function Search({
  project,
  store,
  onCorpusRefusal,
}: {
  project: string;
  store: Store | undefined;
  onCorpusRefusal: (err: unknown) => void;
}) {
  const headers = useOrg();
  const [query, setQuery] = useState('');
  const search = useMutation({
    mutationFn: () =>
      unwrap(
        searchKnowledge({
          headers,
          path: { project },
          body: { query, storeId: store?.id, limit: 10 },
        }),
      ),
    onError: onCorpusRefusal,
  });

  return (
    <Stack>
      <Title order={4}>Search</Title>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (query) search.mutate();
        }}
      >
        <Group align="flex-end">
          <TextInput
            style={{ flex: 1 }}
            placeholder="Ask what an agent would ask"
            description={
              store ? `Nearest passages in ${store.name}.` : 'Nearest passages across every store in the project.'
            }
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
          <Button type="submit" disabled={!query} loading={search.isPending}>
            Search
          </Button>
        </Group>
      </form>
      <Refusal error={search.error} />
      {search.data?.hits.length === 0 && <Text c="dimmed">No passages.</Text>}
      {search.data?.hits.map((h) => (
        <Card withBorder key={`${h.documentId}-${h.chunkIndex}`}>
          <Group justify="space-between" mb={4}>
            <Text size="sm" fw={500}>
              {h.documentTitle}
              {h.headingPath && (
                <Text span size="xs" c="dimmed">
                  {' '}
                  — {h.headingPath}
                </Text>
              )}
            </Text>
            <Badge variant="light">{h.score.toFixed(3)}</Badge>
          </Group>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {h.content}
          </Text>
        </Card>
      ))}
    </Stack>
  );
}
